import {Injectable} from '@angular/core';

import {
  FourDService,
  Account,
  Folder,
  Dataset,
  JobBatch,
  Transform,
  TransformGroup,
  ChunkedFileUploader,
  Job,
  Project,
  TransformType,
  Widget,
  AccountPlan
} from '4d-mapper';
import {forkJoin, Observable, of, Subject, throwError, concat, from} from 'rxjs';
import {debounceTime, flatMap, map, tap, filter, take} from 'rxjs/operators';
import {  User } from 'vasat';
import { ProjectEvent, SyncAgent } from './map-event.objects';
import { Store, ArrayStore, BaseEvent, BaseSimpleEvent} from 'vasat/dist/src/veflux';
import { environment } from '../../../environments/environment';
import { ACLFeatureSet } from '../../commons/acl/acl.service';
import { XsIdentityInterface } from '@src/app/xs/interfaces/identify.interface';

export interface FileUpload{
  dataset:Dataset
  file:File
  progress:number
  uploader?:ChunkedFileUploader
}

export interface TaskProcessing{
  task:Job|Dataset|SyncAgent
  progress:number
  status:string
}


export class ProfileEvent extends BaseSimpleEvent<User>{}
export class AccountEvent extends BaseSimpleEvent<Account>{}
export class FolderEvent extends BaseEvent<Folder,'collapsed'|'expanded'|'selected'|'unselected'>{}
export class FileEvent extends BaseEvent<Dataset,'processing'|'processing_finished'|'processing_error'|'uploading'|'upload_error'|'upload_finished'|'selected'|'unselected'|'deleted'>{}
export class ProcessingEvent extends BaseEvent<TaskProcessing,'processing'|'processing_finished'|'processing_error'|'uploading'|'upload_error'|'upload_finished'>{}

export class UploadEvent extends BaseEvent<FileUpload,'uploading'|'upload_error'|'upload_finished'|'upload_ex'|'abort'>{}

export class ACLFeatureEvent extends BaseSimpleEvent<ACLFeatureSet>{}


@Injectable()
export class FileDataService implements XsIdentityInterface {

  __ID = XsIdentityInterface.__init(this);


  // globals (on Ready)
  private _teams = new ArrayStore(AccountEvent)
  private _profile = new Store(ProfileEvent)
  private _account = new Store(AccountEvent)

  private _team = new Store(AccountEvent)
  private _sampleAccount = new Store(AccountEvent)
  
  private _uploadingQueue = new ArrayStore(UploadEvent)
  private _uploadingFiles = new ArrayStore(UploadEvent)
  private _processingItems = new ArrayStore(ProcessingEvent)

  /* Should this even be here, projects loading on the fly maybe? */
  private _projects = new ArrayStore(ProjectEvent)

  private _acl = new ArrayStore(ACLFeatureEvent)
  
  constructor(public fourD: FourDService) {
      this.initUploadQueueLogic()
  }

  onReady(){
    return this.profile.events.pipe(
      filter(p => p.event == 'ADD'),
      take(1)
    )
  }
  onTeamReady(){
    return this.team.events.pipe(
      filter(p => p.event == 'ADD'),
      take(1)
    )
  }
  init(){
    return this.fourD.onReady().pipe(tap(t => {
      this.teams.values = this.fourD.teams
      this.account.value = this.fourD.activeTeam
      this._sampleAccount.value = this.fourD.sampleAccount
      this.profile.value = this.fourD.activeUser
    }))
  }

  initTeam(a:Account){
    if(this.team.value == a)
      return of(a)
    
    // set to null so items can display loading screens if they need to
    this.team.value = null
    return this.fourD.setActiveTeam(a.uid).pipe(
      flatMap(_ => 
        forkJoin([
          this.fourD.V.searchByObject(Project).equals("account",a.id).sortBy('dateUpdated',true).useCache(true).limit(202).queryObservable().pipe(
            tap(res => this.projects.values = res.items)
          ),
          this.refreshWidgets(a)
        ])
        
      ),  
      map(res => {
        this.team.value = a
        return a
      })
    )
  }

  refreshWidgets( a?:Account,p?:Project){
    return this.fourD.V.api(
      p?'/api/Project/' + p.id + '.access'
      :
      '/api/Account/' + (a ? a.id : ( this.team && this.team.value ? this.team.value.id : 0 )) + '.access'
      ).pipe(
      tap(r => {
        this.acl.values = r
      }))
  }
  destroy(){
  
  }


  private CONCURRENT_ULs = 5
  uploadFiles(files:File[],dest: Folder) {
  

    concat(files.map(file => {
      var name = file.name;
      var meta:any = {
        name: name,
        folder: dest,
        incomplete_offset: 0,
        incomplete_seq: 0,
        incomplete_total: length,
        incomplete: true
      }
    
      var blank = new Dataset(this.fourD.V).set(meta);
      
      return blank.saveObservable().pipe(map(res => {
        this.fourD.V.registerCacheClass(Dataset,[blank])
        return {
          dataset:res,
          file:file,
          progress:0
        }
      })).subscribe(res => {
        if(this.uploadingFiles.values.length < this.CONCURRENT_ULs)
          this.uploadingFiles.set(res)
        else 
          this.uploadingQueue.set(res)
      })

    })).subscribe()
 

  }
/*
job_batch_update
Sock cast SocketCmd(job_batch_update,Map(name -> Thu Feb 27 12:27:16 AEDT 2020, job_name -> DEFAULT t, job_id -> 137, info -> processing, id -> 152, progress -> 0, status -> processing)) None

*/

  /**
   * Queue logic for file uploads, makes sure only a certain number of files are uplaoding at once, queuees the rest
   */
  private initUploadQueueLogic(){
  

    this.uploadingFiles.events.subscribe(ev => {
      // when file is queued, construct the actual upload to server
      if(ev.event == 'ADD'){
        var c = new ChunkedFileUploader(this.fourD.V,this.fourD.V.config.host + 
          '/api/Dataset/' + ev.item.dataset.id + '.chunked/' + ev.item.file.name
        ,15000000)
        ev.item.uploader = c
        c.progressObservable.subscribe(s => {
          ev.item.progress = s
          this.uploadingFiles.emmit(ev.item,'UPDATE',{progress:s})  
        })
        c.uploadObservable(ev.item.file).subscribe(s => {
          if(s.finished){
            this.uploadingFiles.emmit(ev.item,'upload_finished',s)
            if(s.status == 204){
              this.processingItems.add({
                task:ev.item.dataset,
                progress:1,
                status:'Detecting File Type'
              })
            }
          }
          else{
            this.uploadingFiles.emmit(ev.item,'upload_error',s)
          }
          this.uploadingFiles.remove(ev.item)
        },e => {
          this.uploadingFiles.emmit(ev.item,'upload_ex',e)
          this.uploadingFiles.remove(ev.item)
        })
       
      }
      else if(ev.event == 'abort'){
        ev.item.uploader.cancel()
      }
      // if theres more items on queu grab next one
      else if(ev.event == 'REMOVE' && this.uploadingQueue.values.length){
        var next = this.uploadingQueue.values[0]
        this.uploadingQueue.remove(next)
        this.uploadingFiles.set(next)
      }
    })

  }


  // store syncers
  private eq(a:Widget,b:Widget):boolean {
    return a.uid == b.uid
  }
  sync<A>(e:BaseSimpleEvent<A>,b:ArrayStore<A,any,BaseSimpleEvent<A>>) {
      if(e.event == 'ADD'){
        b.add(e.item)
      }
      else if(e.event == 'REMOVE'){
        b.remove(e.item)
      }
      else if(e.event == 'UPDATE'){
        b.emmit(e.item,'UPDATE')
      }
  }


  // Getters

  get teams(){
    return this._teams
  }
  get profile(){
    return this._profile
  }
  get account(){
    return this._account
  }
  get team(){
    return this._team
  }
  get projects(){
    return this._projects
  }

  get processingItems(){
    return this._processingItems
  }
 
  get uploadingFiles(){
    return this._uploadingFiles
  } 
  get uploadingQueue(){
    return this._uploadingQueue
  } 
  get acl(){
    return this._acl
  }
 
}
